import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  computed,
  effect,
  inject,
  signal,
  untracked,
} from '@angular/core';
import { TranslocoPipe } from '@jsverse/transloco';

import { PaperButtonComponent, PaperIconComponent } from '@ricoh-paper/angular';
import { PushRegistrationService } from '../../../../core/services/push-registration.service';
import { NotificationPreferencesService } from '../../../../core/services/notification-preferences.service';
import { UserPreferencesService } from '../../../../core/services/user-preferences.service';
import { ShellStateService } from '../../../../core/services/shell-state.service';
import { AnalyticsService } from '../../../../core/services/analytics.service';
import { ANALYTICS_EVENTS } from '../../../../core/analytics/events';
import {
  PUSH_PRIMER_STORAGE_KEY,
  buildPushPrimerDecision,
  parsePushPrimerDecision,
  shouldShowPushPrimer,
} from './push-primer.logic';

/**
 * First-run notification primer — a dismissible, non-modal card in the
 * bottom-right corner of the main app shell. It frames push notifications as
 * an intrinsic part of the product and invites the user to enable them.
 *
 * RESPONSIBILITY:
 *   - Decide (once, ~5s after load) whether to surface the primer via the
 *     pure `shouldShowPushPrimer()` gate.
 *   - Run `PushRegistrationService.enable()` from the user gesture and reflect
 *     its pending / success / error / blocked states inline.
 *   - Persist the user's decision so the primer never reappears.
 *
 * It owns NO push lifecycle of its own — that all lives in
 * PushRegistrationService. This is presentation + a one-shot first-run gate.
 *
 * MOUNTED: once, at the bottom of AppShellComponent's template (the MAIN app
 * shell — NOT the AdminShell at /settings|/admin, NOT the login page). It
 * self-gates, so it is inert until its conditions hold.
 *
 * ACCESSIBILITY: non-modal, so `role="complementary"` + an aria-live polite
 * region for state changes — never a `role="dialog"` (that would trap focus
 * inappropriately for a passive invitation). The close control is a labelled,
 * keyboard-reachable ghost icon button. Escape is intentionally NOT bound.
 */
@Component({
  selector: 'im-push-primer',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [TranslocoPipe, PaperButtonComponent, PaperIconComponent],
  templateUrl: './push-primer.component.html',
  styleUrl: './push-primer.component.scss',
  host: {
    // Keep the journey clean: this surface fires its own typed events.
    class: 'ph-no-capture',
  },
})
export class PushPrimerComponent {
  private readonly push = inject(PushRegistrationService);
  private readonly prefs = inject(NotificationPreferencesService);
  private readonly userPrefs = inject(UserPreferencesService);
  private readonly shell = inject(ShellStateService);
  private readonly analytics = inject(AnalyticsService);
  private readonly destroyRef = inject(DestroyRef);

  /** Whether the card is mounted/visible. Flips true once the gate passes. */
  protected readonly visible = signal(false);

  /** Action-phase state for the Enable button + status line. */
  protected readonly phase = signal<'idle' | 'pending' | 'success' | 'error'>(
    'idle',
  );

  /** Live permission + error straight from the push service. */
  protected readonly permission = this.push.permission;
  protected readonly serviceError = this.push.error;

  /** Permission became 'denied' mid-flow → hide Enable, show a blocked line. */
  protected readonly blocked = computed(() => this.permission() === 'denied');

  private shownTimer: ReturnType<typeof setTimeout> | null = null;
  private successTimer: ReturnType<typeof setTimeout> | null = null;

  /** Set once the user has answered, so a later re-run cannot re-arm the timer. */
  private settled = false;

  constructor() {
    /*
     * WAIT for first run rather than racing it.
     *
     * This was a bare 5s timer, and its comment said the delay existed so the
     * primer would not "compete with first paint / onboarding" — but first run
     * is a two-minute flow, so five seconds lost that race every time. The card
     * is fixed to the bottom-right corner and sits above the sheet, so a new
     * user met the same question twice at once, with the worse-worded one
     * covering the sheet's primary button.
     *
     * The effect tracks ONLY `onboardingComplete`; the scheduling it triggers
     * is untracked, and the evaluation itself runs later from a timer, so
     * nothing this writes can dirty the effect that scheduled it.
     */
    effect(() => {
      const firstRunOpen = !this.userPrefs.onboardingComplete();
      // Same "wait, don't fight" treatment for the bottom-right corner: while
      // a surface owns it, the primer stays down and re-arms when that
      // surface lets go. See PushPrimerConditions.bottomAnchorClaimed.
      const anchorClaimed = this.shell.bottomAnchorClaimed();
      untracked(() => this.schedule(firstRunOpen || anchorClaimed));
    });

    this.destroyRef.onDestroy(() => {
      if (this.shownTimer) clearTimeout(this.shownTimer);
      if (this.successTimer) clearTimeout(this.successTimer);
    });
  }

  /**
   * Arm (or disarm) the gate evaluation. While first run is open the primer
   * stays down and no timer is held; the effect above calls back in when the
   * sheet closes, so a brand-new user still gets asked — just once, and after
   * the flow rather than over it.
   */
  private schedule(firstRunOpen: boolean): void {
    if (this.settled || this.visible()) return;

    if (this.shownTimer) {
      clearTimeout(this.shownTimer);
      this.shownTimer = null;
    }
    if (firstRunOpen) return;

    // Gentle: the check is cheap and synchronous; the delay just keeps it clear
    // of first paint and of the sheet's own closing animation.
    this.shownTimer = setTimeout(() => void this.evaluate(), 5000);
  }

  /** Read all gate inputs and reveal the card when every condition holds. */
  private async evaluate(): Promise<void> {
    const priorDecision = parsePushPrimerDecision(this.readStorage());

    // The push preference is read cheaply from whatever the prefs service has
    // already loaded — we do NOT trigger a fetch just for the primer. If the
    // value is unknown (service not yet loaded) we don't block on it; the
    // 'never explicitly OFF' rule only suppresses when we positively know it's
    // off (see shouldShowPushPrimer / globalPushPref handling).
    const globalPushPref = this.prefs.preferences().globalPush;

    const conditions = {
      supported: this.push.supported(),
      permission: this.permission(),
      priorDecision,
      globalPushPref,
      // Re-read rather than trusting the value that armed the timer: five
      // seconds is long enough for the user to have reopened the flow — or
      // to have opened the assistant.
      firstRunOpen: !this.userPrefs.onboardingComplete(),
      bottomAnchorClaimed: this.shell.bottomAnchorClaimed(),
    };
    const show = shouldShowPushPrimer(conditions);
    if (!show) {
      // A silent no-show is undebuggable — say WHY (one console line per
      // app load, ~5s in).
      // Same order as shouldShowPushPrimer's own checks, so the reason names
      // the condition that actually stopped it.
      const reasons: readonly [boolean, string][] = [
        [conditions.firstRunOpen, 'first run is open — waiting for it to finish'],
        [
          conditions.bottomAnchorClaimed,
          'a surface owns the bottom-right corner — waiting for it to close',
        ],
        [!conditions.supported, 'push not supported in this browser'],
        [
          conditions.permission !== 'default',
          `browser permission already '${conditions.permission}'`,
        ],
        [
          conditions.priorDecision !== null,
          `prior decision '${conditions.priorDecision?.state}' (clear localStorage['imp.push-primer'] to re-show)`,
        ],
      ];
      const reason =
        reasons.find(([hit]) => hit)?.[1] ?? 'push preference explicitly off';
      console.info(`[PushPrimer] not showing — ${reason}`);
      return;
    }

    this.visible.set(true);
    this.analytics.capture(ANALYTICS_EVENTS.PUSH_PRIMER_SHOWN, {});
  }

  /** Primary action — runs the real registration flow from the user gesture. */
  protected async onEnable(): Promise<void> {
    if (this.phase() === 'pending') return;
    this.phase.set('pending');

    let granted = false;
    try {
      granted = await this.push.enable();
    } catch {
      // enable() is defensive and shouldn't throw, but never let a rejection
      // leave the button stuck in 'pending'.
      granted = false;
    }

    this.analytics.capture(ANALYTICS_EVENTS.PUSH_PRIMER_ENABLED, { granted });

    if (granted) {
      this.settled = true;
      this.persist('enabled');
      this.phase.set('success');
      // Brief success state, then auto-hide.
      this.successTimer = setTimeout(() => this.visible.set(false), 2500);
      return;
    }

    // Failure (or permission not granted): keep the card so they can retry.
    // The status line surfaces the service error / blocked message. We do NOT
    // persist here — a transient failure shouldn't permanently suppress the
    // primer. A hard 'denied' is reflected by the blocked state.
    this.phase.set('error');
  }

  /** "Not now" text action. */
  protected onNotNow(): void {
    this.dismiss('not-now');
  }

  /** Corner close (X) — same outcome as Not now. */
  protected onClose(): void {
    this.dismiss('close');
  }

  private dismiss(via: 'not-now' | 'close'): void {
    this.settled = true;
    this.analytics.capture(ANALYTICS_EVENTS.PUSH_PRIMER_DISMISSED, { via });
    this.persist('dismissed');
    this.visible.set(false);
  }

  private persist(state: 'dismissed' | 'enabled'): void {
    try {
      localStorage.setItem(
        PUSH_PRIMER_STORAGE_KEY,
        JSON.stringify(buildPushPrimerDecision(state)),
      );
    } catch {
      // Private-mode / storage-disabled: the worst case is the primer may
      // reappear next session. Not worth surfacing.
    }
  }

  private readStorage(): string | null {
    try {
      return localStorage.getItem(PUSH_PRIMER_STORAGE_KEY);
    } catch {
      return null;
    }
  }
}
